// initial state

import {
  FROM_ROOM_TAG_PARAM, parseFromRoomTagSlug, takeEventTypeChipSwitch,
} from '@/modules/public/search/services/event-type-chips';
import { HOURS_MAP_24 } from '@/services/hours-map.js';

import {
  featuresListToMap,
  filterToCountry, getUrlParam, isFutureDate, isMapViewForcedByUrl, isMobile,
} from '../services.js';
import options from './options.json';

const config = window._VUE_SEARCH_CONFIG;
const eventTypeChipSwitch = takeEventTypeChipSwitch();

config.l10n = config.l10n || {};
const l10n = {
  ISOCountryCode: config.l10n.ISOCountryCode,
  currencySign: config.l10n.currencySign,
  weekStart: config.l10n.weekStart,
  timeFormat: config.l10n.timeFormat,
  datepickerFormat: config.l10n.datepickerFormat,
  countrySubdir: config.l10n.countrySubdir,
};

const state = {
  filters: {
    eventType: {
      value: config.roomTagSlug,
    },
    people: {
      value: +(config.queryParams.people || 0),
      error: false,
    },
    layout: {
      value: config.queryParams.room_layout,
    },
    date: {
      value: isFutureDate(config.queryParams.date) ? config.queryParams.date : null,
    },
    timeFrom: {
      value: config.queryParams.time_from,
    },
    timeTo: {
      value: config.queryParams.time_to,
    },
    location: {
      value: config.queryParams.neighbourhood,
    },
    priceRange: {
      value: {
        from: +config.queryParams.min_price || null,
        to: +config.queryParams.max_price || null,
      },
    },
    customPriceRange: {
      value: (() => {
        // backend doesn't pass price_range in queryParams, so read directly from URL
        const priceRangeFromUrl = getUrlParam('price_range');
        return config.queryParams.price_range || priceRangeFromUrl || null;
      })(),
    },
    customRoomFeatures: {
      value: [],
    },
    priceMethod: {
      value: config.queryParams.price_method || getUrlParam('price_method') || null,
    },
    superVenuesOnly: {
      value: config.queryParams.supervenues_only,
    },
    flexibleCancellation: {
      value: config.queryParams.flexible_cancellation_only,
    },
    noAgeRestriction: {
      value: config.queryParams.no_age_restriction ?? false,
    },
    venueParts: {
      value: config.queryParams.venue_parts ?? [],
    },
    venueTypes: {
      value: config.queryParams.venue_types.map(val => parseInt(val, 10)) ?? [],
    },
    accessibilityFeatures: {
      value: featuresListToMap(config.queryParams.accessibility_features) ?? {},
    },
    catering: {
      label: 'Catering and drinks',
      value: config.queryParams.catering_options || [],
      options: config.availableCateringOptions.map(option => ({ ...option, param: 'catering' })),
      expanded: false,
    },
    roomFeatures: {
      label: 'Facilities',
      value: (() => {
        const features = config.queryParams.room_features;
        if (!features || typeof features !== 'object') return [];
        return Object.keys(features).filter(key => features[key] || features[key] === '');
      })(),
      options: options.roomFeatures,
      expanded: false,
    },
    roomTags: {
      value: [...new Set(
        [].concat(config.queryParams.room_tags ?? [])
          .map(Number)
          .filter(id => Number.isInteger(id) && id > 0),
      )],
    },
    allowedEvents: {
      label: 'Allowed Events',
      value: config.queryParams.allowed_events || [],
      options: filterToCountry(options.allowedEvents, l10n.ISOCountryCode),
      expanded: false,
    },
    venueFeatures: {
      label: 'Parking and Accommodation',
      value: config.queryParams.$venueFeatures || [],
      options: options.venueFeatures,
    },
  },
  selectedFiltersReactive: false,
  rooms: null,
  roomsReducedFilters: [],
  reducedFilters: [],
  loadingReducedFilters: true,
  boundaries: {
    latitude_from: config.queryParams.latitude_from,
    latitude_to: config.queryParams.latitude_to,
    longitude_from: config.queryParams.longitude_from,
    longitude_to: config.queryParams.longitude_to,
  },
  page: +config.queryParams.page || 1,
  perPage: 36,
  canProvideAlcoholOptionId: 8,
  totalDisplayedRooms: window._VUE_SEARCH_DATA.meta.hits_count, // total of search results
  totalRooms: window._VUE_SEARCH_DATA.meta.hits_count, // total of map rooms
  quickFilters: window._VUE_SEARCH_CONFIG?.quickFilters || [],
  loading: false,
  metaHeader: null,
  eventType: window._VUE_SEARCH_DATA.meta.room_tag,
  loadedEventType: window._VUE_SEARCH_DATA.meta.room_tag,
  eventTypeSlug: config.roomTagSlug,
  // event type the search came from when an event-type chip switched it, so unselecting the chip can go back
  fromRoomTagSlug: parseFromRoomTagSlug(getUrlParam(FROM_ROOM_TAG_PARAM)),
  // chips selected before an event-type chip reloaded the search – kept in the row while they stay selected
  carriedQuickFilters: eventTypeChipSwitch?.quickFilters ?? [],
  eventTypeSwitchedByChip: eventTypeChipSwitch !== null,
  timeSlots: HOURS_MAP_24,
  isMobile: isMobile(),
  showAdvancedFilters: false,
  mobileView: isMapViewForcedByUrl() ? 'map' : 'results', // default mobile view
  hashId: window._VUE_SEARCH_DATA.meta.hash || null,
  metaPricingMethodCounter: window._VUE_SEARCH_DATA.meta.pricing_method_counter,
  l10n,
  standingLayoutKey: 7,
  sittingLayoutKey: config.sittingLayoutKey,
  roomTagsWithoutPricingMethodFilter: config.roomTagsWithoutPricingMethodFilter,
  routes: {
    search: config.routes.search,
    home: config.routes.home,
  },
  initialLocation: Object.freeze({
    neighbourhood: config.queryParams.neighbourhood,
    latitude_from: config.queryParams.latitude_from,
    latitude_to: config.queryParams.latitude_to,
    longitude_from: config.queryParams.longitude_from,
    longitude_to: config.queryParams.longitude_to,
  }),
  // map zoom reflected from google map for analytics purposes
  mapZoom: null,
  christmasDiningEventTypes: options.christmasDiningEventTypes,
  christmasEventTypes: options.christmasEventTypes,
  priceBoundaries: {
    per_person: [0, 200],
    default: [0, 10000],
  },
  utmSource: getUrlParam('utm_source') || '',
  utmMedium: getUrlParam('utm_medium') || '',
  utmCampaign: getUrlParam('utm_campaign') || '',
  utmContent: getUrlParam('utm_content') || '',
  utmTerm: getUrlParam('utm_term') || '',
  trackingCity: null,
  eventCategory: config.eventCategory,
  locationType: config.locationType,
  venuePartsList: config.venuePartsList,

  venueTypesPerEventCategoryConfig: config.venueTypesPerEventCategoryConfig,

  debugFilterMode: false, // DMS toolbar feature to gray out filter-out rooms instead of not displaying them
  debugFilterModeRooms: null, // Rooms that match filters map with booleans
  debugFilterModeMaxRooms: 200, // Rooms that match filters map with booleans

  managersLastActivityOnline: {},

  triggerSection: config.queryParams.trigger_section, // used for triggering/opening a specific filter section on the page

  // TM-4364
  highlightedRoomId: config.queryParams.highlighted_room_id,
  highlightedRoomData: window._VUE_SEARCH_DATA.highlightedRoomData,

  experimentChipsFiltersWasMounted: false,
};

state.rooms = window._VUE_SEARCH_DATA.rooms;
state.displayedRooms = state.isMobile ? 6 : 12;

if (!state.filters.priceRange.value.from) {
  state.filters.priceRange.value.from = state.filters.priceMethod.value === 'per_person'
    ? state.priceBoundaries.per_person[0]
    : state.priceBoundaries.default[0];
}

if (!state.filters.priceRange.value.to) {
  state.filters.priceRange.value.to = state.filters.priceMethod.value === 'per_person'
    ? state.priceBoundaries.per_person[1]
    : state.priceBoundaries.default[1];
}

// data tracking variables
window.__DATA_TRACKING_VAR__TAG_SLUG = config.roomTagSlug;

export default state;
